import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useChat } from './_social/ChatContext';
import ChatSelector from './_social/ChatSelector';
import ChatPanel from './_social/ChatPanel';
import FeedContainer from './_social/FeedContainer';
import { 
  Typography, 
  Box, 
  Tabs, 
  Tab, 
  useMediaQuery, 
  useTheme,
  Paper,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import ForumIcon from '@mui/icons-material/Forum';
import PeopleIcon from '@mui/icons-material/People';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

// Styling components
const FullWidthContainer = styled.div`
  height: calc(100vh - 64px); 
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const SectionHeader = styled(Box)`
  background: #1e1e1e;
  padding: 16px 20px;
  border-bottom: 1px solid #333;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SectionContent = styled(Box)`
  flex: 1;
  overflow: hidden;
  background: #121212;
`;

// Resizer component
const ResizeHandle = styled.div`
  width: 8px;
  background-color: #2a2a2a;
  cursor: col-resize !important;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::after {
    content: '';
    width: 2px;
    height: 40px;
    background-color: #666;
    border-radius: 1px;
  }
  
  &:hover, &.active {
    background-color: #444;
  }
  
  &:hover::after, &.active::after {
    background-color: #999;
  }
`;

// Collapsed section with vertical text
const CollapsedSection = styled(Box)`
  background-color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  
  &:hover {
    background-color: #2a2a2a;
  }
`;

const VerticalText = styled(Typography)`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  white-space: nowrap;
  user-select: none;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 500;
`;

const Social = () => {
  const { setIsChatOpen } = useChat();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Width management
  const COLLAPSED_WIDTH = 3; // Width when collapsed (%)
  const MIN_WIDTH = 15; // Minimum width (%)
  const DEFAULT_FEED_WIDTH = 45; // Default width for feed (%)
  
  // Local storage keys - create constants to avoid typos
  const STORAGE_KEY_FEED_WIDTH = 'socialFeedWidth';
  const STORAGE_KEY_FEED_COLLAPSED = 'socialFeedCollapsed';
  const STORAGE_KEY_CHAT_COLLAPSED = 'socialChatCollapsed';
  
  // Improved local storage helpers
  const getFromLocalStorage = (key, defaultValue) => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return defaultValue;
      
      // Try to parse as JSON first, fall back to string
      try {
        return JSON.parse(item);
      } catch (e) {
        return item;
      }
    } catch (error) {
      console.warn(`Error reading ${key} from localStorage:`, error);
      return defaultValue;
    }
  };
  
  const saveToLocalStorage = (key, value) => {
    try {
      if (typeof value === 'object') {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        localStorage.setItem(key, String(value));
      }
    } catch (error) {
      console.warn(`Error saving ${key} to localStorage:`, error);
    }
  };
  
  // Initialize states with improved localStorage helper
  const [isFeedCollapsed, setIsFeedCollapsed] = useState(() => 
    getFromLocalStorage(STORAGE_KEY_FEED_COLLAPSED, false)
  );
  
  const [isChatAreaCollapsed, setIsChatAreaCollapsed] = useState(() => 
    getFromLocalStorage(STORAGE_KEY_CHAT_COLLAPSED, false)
  );
  
  // Initialize feed width with improved handling
  const [feedWidth, setFeedWidth] = useState(() => {
    const savedWidth = getFromLocalStorage(STORAGE_KEY_FEED_WIDTH, DEFAULT_FEED_WIDTH);
    // Ensure it's a valid number between MIN_WIDTH and 100-MIN_WIDTH
    const parsedWidth = parseFloat(savedWidth);
    if (isNaN(parsedWidth)) return DEFAULT_FEED_WIDTH;
    
    // If the saved width doesn't make sense with the collapsed states, use DEFAULT_FEED_WIDTH
    if (isFeedCollapsed && parsedWidth > COLLAPSED_WIDTH) return COLLAPSED_WIDTH;
    if (isChatAreaCollapsed && parsedWidth < 100 - COLLAPSED_WIDTH) return 100 - COLLAPSED_WIDTH;
    
    return parsedWidth;
  });
  
  // References
  const containerRef = useRef(null);
  const feedColumnRef = useRef(null);
  const chatAreaRef = useRef(null);
  
  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);

  // Effect for updating localStorage when states change
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEY_FEED_COLLAPSED, isFeedCollapsed);
  }, [isFeedCollapsed]);
  
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEY_CHAT_COLLAPSED, isChatAreaCollapsed);
  }, [isChatAreaCollapsed]);
  
  // Debounced saving of feed width to avoid excessive writes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only save significant width changes when not collapsing/expanding
      if (!isAnimating) {
        saveToLocalStorage(STORAGE_KEY_FEED_WIDTH, feedWidth);
      }
    }, 100); // 100ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [feedWidth, isAnimating]);

  useEffect(() => {
    setIsChatOpen(true);
    return () => {
      if (window.location.pathname !== '/social') {
        setIsChatOpen(false);
      }
    };
  }, [setIsChatOpen]);

  // Calculate chat area width based on feed width
  const chatAreaWidth = isFeedCollapsed 
    ? (100 - COLLAPSED_WIDTH) 
    : (isChatAreaCollapsed ? COLLAPSED_WIDTH : (100 - feedWidth));

  // Function to handle resizing - updated to store position in localStorage
  function onMouseDown(e) {
    e.preventDefault();
    e.stopPropagation();
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    const startX = e.clientX;
    const startWidth = feedColumnRef.current ? feedColumnRef.current.offsetWidth : 0;
    const containerWidth = containerRef.current ? containerRef.current.offsetWidth : window.innerWidth;
    
    if (startWidth === 0 || containerWidth === 0) return;
    
    setIsAnimating(true);
    
    function onMouseMove(e) {
      if (!containerRef.current) return;
      
      const deltaX = e.clientX - startX;
      const newWidthPx = startWidth + deltaX;
      const newWidthPercent = (newWidthPx / containerWidth) * 100;
      
      // Apply constraints
      if (newWidthPercent <= MIN_WIDTH) {
        setFeedWidth(COLLAPSED_WIDTH);
        setIsFeedCollapsed(true);
        setIsChatAreaCollapsed(false);
        return;
      }
      
      if (100 - newWidthPercent <= MIN_WIDTH) {
        setFeedWidth(100 - COLLAPSED_WIDTH);
        setIsFeedCollapsed(false);
        setIsChatAreaCollapsed(true);
        return;
      }
      
      // Normal resize
      setFeedWidth(newWidthPercent);
      setIsFeedCollapsed(false);
      setIsChatAreaCollapsed(false);
    }
    
    function onMouseUp() {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      
      // Explicitly save final state to localStorage
      saveToLocalStorage(STORAGE_KEY_FEED_WIDTH, feedWidth);
      saveToLocalStorage(STORAGE_KEY_FEED_COLLAPSED, isFeedCollapsed);
      saveToLocalStorage(STORAGE_KEY_CHAT_COLLAPSED, isChatAreaCollapsed);
      
      // Reset animation state after a short delay to allow transitions to complete
      setTimeout(() => setIsAnimating(false), 300);
    }
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  // Toggle functions for expanding/collapsing - updated to prevent both being collapsed
  // Improved toggle functions that work independently
  const toggleFeedCollapse = () => {
    if (isAnimating) return; // Prevent double-clicks during animation
    setIsAnimating(true);
    
    // If feed is collapsed, expand it
    if (isFeedCollapsed) {
      setIsFeedCollapsed(false);
      setFeedWidth(DEFAULT_FEED_WIDTH);
      setTimeout(() => setIsAnimating(false), 300);
      return;
    }
    
    // If feed is open and chat is collapsed (feed is maximized)
    // then restore both to default view instead of collapsing feed
    if (isChatAreaCollapsed) {
      setIsChatAreaCollapsed(false);
      setFeedWidth(DEFAULT_FEED_WIDTH); // Reset to default balanced view
      setTimeout(() => setIsAnimating(false), 300);
      return;
    }
    
    // Regular collapse behavior when both sections are visible
    setFeedWidth(COLLAPSED_WIDTH);
    setTimeout(() => {
      setIsFeedCollapsed(true);
      setIsAnimating(false);
    }, 300);
  };
  
  const toggleChatAreaCollapse = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    // If chat is collapsed, expand it
    if (isChatAreaCollapsed) {
      setIsChatAreaCollapsed(false);
      setFeedWidth(DEFAULT_FEED_WIDTH);
      setTimeout(() => setIsAnimating(false), 300);
      return;
    }
    
    // If chat is open and feed is collapsed (chat is maximized)
    // then restore both to default view instead of collapsing chat
    if (isFeedCollapsed) {
      setIsFeedCollapsed(false);
      setFeedWidth(DEFAULT_FEED_WIDTH); // Reset to default balanced view
      setTimeout(() => setIsAnimating(false), 300);
      return;
    }
    
    // Regular collapse behavior when both sections are visible
    setFeedWidth(100 - COLLAPSED_WIDTH);
    setTimeout(() => {
      setIsChatAreaCollapsed(true);
      setIsAnimating(false);
    }, 300);
  };

  // Get the correct tooltip text based on current state
  const getFeedButtonTooltip = () => {
    if (isFeedCollapsed) return "Expand Feed";
    if (isChatAreaCollapsed) return "Restore Default View";
    return "Collapse Feed";
  };

  const getChatButtonTooltip = () => {
    if (isChatAreaCollapsed) return "Expand Chat";
    if (isFeedCollapsed) return "Restore Default View";
    return "Collapse Chat";
  };

  // Get the correct icon based on current state - updated logic
  const getFeedButtonIcon = () => {
    if (isChatAreaCollapsed) {
      // When feed is maximized and chat is collapsed, 
      // the restore button should point LEFT to indicate "restore chat"
      return <ChevronLeftIcon />;
    }
    // Normal collapse icon - always points left
    return <ChevronLeftIcon />;
  };

  const getChatButtonIcon = () => {
    if (isFeedCollapsed) {
      // When chat is maximized and feed is collapsed,
      // the restore button should point RIGHT to indicate "restore feed"
      return <ChevronRightIcon />;
    }
    // Normal collapse icon - always points right
    return <ChevronRightIcon />;
  };

  // Mobile navigation handlers
  const handleMobileSectionChange = (event, newValue) => {
    setMobileSection(newValue);
  };

  const handleSelectConversation = (conversationId) => {
    setActiveConversation(conversationId);
  };

  // Add state for compact chat selector
  const [isCompactChatList, setIsCompactChatList] = useState(false);
  
  // Function to check and update chat list display mode
  const updateChatListDisplayMode = () => {
    if (chatAreaRef.current) {
      // If available width is less than threshold, switch to compact mode
      const availableWidth = chatAreaRef.current.offsetWidth;
      // Threshold: enough space for both chat panel and conversation list
      const shouldCompact = availableWidth < 600; // Adjust threshold as needed
      setIsCompactChatList(shouldCompact);
    }
  };
  
  // Call this when chat area width changes
  useEffect(() => {
    if (!isMobile && !isChatAreaCollapsed) {
      updateChatListDisplayMode();
    }
  }, [chatAreaWidth, isMobile, isChatAreaCollapsed]);
  
  // Also update on window resize
  useEffect(() => {
    const handleResize = () => updateChatListDisplayMode();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Render collapsed sections
  const renderCollapsedFeed = () => (
    <CollapsedSection
      onClick={toggleFeedCollapse}
      sx={{ 
        width: `${COLLAPSED_WIDTH}%`, 
        height: '100%',
        transition: 'width 0.3s ease-in-out, transform 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateX(2px)' // Slight movement to the right on hover
        }
      }}
    >
      <Tooltip title="Expand Feed" placement="right">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
          <RssFeedIcon sx={{ mb: 1, color: 'white', fontSize: 24 }} />
          <VerticalText variant="body2" color="white" sx={{ mt: 2 }}>
            Team Feed
          </VerticalText>
          <ChevronRightIcon sx={{ mt: 2, color: 'white' }} />
        </Box>
      </Tooltip>
    </CollapsedSection>
  );

  const renderCollapsedChatArea = () => (
    <CollapsedSection
      onClick={toggleChatAreaCollapse}
      sx={{ 
        width: `${COLLAPSED_WIDTH}%`, 
        height: '100%',
        transition: 'width 0.3s ease-in-out, transform 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateX(-2px)' // Slight movement to the left on hover
        }
      }}
    >
      <Tooltip title="Expand Chat" placement="left">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
          <ForumIcon sx={{ mb: 1, color: 'white', fontSize: 24 }} />
          <VerticalText variant="body2" color="white" sx={{ mt: 2 }}>
            Chat
          </VerticalText>
          <ChevronLeftIcon sx={{ mt: 2, color: 'white' }} />
        </Box>
      </Tooltip>
    </CollapsedSection>
  );

  const renderFeedSection = () => {
    return (
      <Box 
        ref={feedColumnRef}
        sx={{ 
          width: `${isFeedCollapsed ? COLLAPSED_WIDTH : feedWidth}%`,
          height: '100%', 
          display: 'flex',
          flexDirection: 'column', 
          borderRight: '1px solid #333',
          position: 'relative',
          transition: 'width 0.3s ease-in-out, transform 0.2s ease-in-out',
          overflow: 'hidden',
        }}
      >
        {!isFeedCollapsed ? (
          // Normal feed view
          <>
            <SectionHeader>
              <Typography variant="h6" color="white">
                Team Feed
              </Typography>
              <Tooltip title={getFeedButtonTooltip()}>
                <IconButton 
                  size="small" 
                  sx={{ color: 'white' }}
                  onClick={toggleFeedCollapse}
                >
                  {getFeedButtonIcon()}
                </IconButton>
              </Tooltip>
            </SectionHeader>
            <SectionContent>
              <FeedContainer />
            </SectionContent>
            <ResizeHandle onMouseDown={onMouseDown} />
          </>
        ) : (
          // Collapsed feed view - vertical icon and text
          <Tooltip title="Expand Feed" placement="right">
            <Box 
              onClick={toggleFeedCollapse}
              sx={{ 
                height: '100%', 
                width: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 3,
                cursor: 'pointer',
                '&:hover': {
                  background: '#2a2a2a',
                  transform: 'translateX(2px)'
                },
                transition: 'background 0.2s, transform 0.3s ease-in-out',
              }}
            >
              <RssFeedIcon sx={{ mb: 1, color: 'white', fontSize: 24 }} />
              <VerticalText variant="body2" color="white" sx={{ mt: 2 }}>
                Team Feed
              </VerticalText>
              <ChevronRightIcon sx={{ mt: 2, color: 'white' }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderChatAreaSection = () => {
    return (
      <Box 
        ref={chatAreaRef}
        data-chat-area="true"
        sx={{ 
          width: `${isChatAreaCollapsed ? COLLAPSED_WIDTH : chatAreaWidth}%`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease-in-out, transform 0.2s ease-in-out',
          overflow: 'hidden',
        }}
      >
        {!isChatAreaCollapsed ? (
          // Normal chat view with button moved to the left side
          <>
            <SectionHeader>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={getChatButtonTooltip()}>
                  <IconButton 
                    size="small" 
                    sx={{ color: 'white', mr: 1.5 }}
                    onClick={toggleChatAreaCollapse}
                  >
                    {getChatButtonIcon()}
                  </IconButton>
                </Tooltip>
                <Typography variant="h6" color="white">
                  Chat
                </Typography>
              </Box>
              <Box>
                {/* Right side - empty for now, can add other controls later */}
              </Box>
            </SectionHeader>
            
            {/* Combined Chat Area Content */}
            <Box sx={{ 
              display: 'flex', 
              height: 'calc(100% - 53px)', 
              overflow: 'hidden',
              flexDirection: 'row'
            }}>
              <Box sx={{ 
                flex: 1,
                height: '100%',
                borderRight: '1px solid #333',
              }}>
                <ChatPanel conversation={activeConversation} />
              </Box>
              
              {/* Conversation list with responsive width */}
              <Box sx={{ 
                width: isCompactChatList ? '70px' : '280px',
                minWidth: isCompactChatList ? '70px' : '280px',
                maxWidth: isCompactChatList ? '70px' : '280px',
                height: '100%',
                transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out, max-width 0.3s ease-in-out',
              }}>
                <ChatSelector 
                  onSelectConversation={handleSelectConversation} 
                  compact={isCompactChatList} 
                />
              </Box>
            </Box>
          </>
        ) : (
          // Collapsed chat view - vertical icon and text
          <Tooltip title="Expand Chat" placement="left">
            <Box 
              onClick={toggleChatAreaCollapse}
              sx={{ 
                height: '100%', 
                width: '100%',
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 3,
                cursor: 'pointer',
                '&:hover': {
                  background: '#2a2a2a',
                  transform: 'translateX(-2px)'
                },
                transition: 'background 0.2s, transform 0.3s ease-in-out',
              }}
            >
              <ForumIcon sx={{ mb: 1, color: 'white', fontSize: 24 }} />
              <VerticalText variant="body2" color="white" sx={{ mt: 2 }}>
                Chat
              </VerticalText>
              <ChevronLeftIcon sx={{ mt: 2, color: 'white' }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Add missing state
  const [activeConversation, setActiveConversation] = useState(null);
  const [mobileSection, setMobileSection] = useState('feed');

  return (
    <FullWidthContainer ref={containerRef}>
      {/* Mobile Navigation */}
      {isMobile && (
        <Tabs
          value={mobileSection}
          onChange={handleMobileSectionChange}
          variant="fullWidth"
          sx={{ borderBottom: '1px solid #333', backgroundColor: '#1e1e1e' }}
        >
          <Tab 
            icon={<RssFeedIcon />} 
            label="Feed" 
            value="feed" 
            sx={{ color: 'white' }} 
          />
          <Tab 
            icon={<ForumIcon />}
            label="Chat" 
            value="chat" 
            sx={{ color: 'white' }} 
          />
        </Tabs>
      )}

      {/* Main Content - Two-column layout */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        height: '100%',
        width: '100%'
      }}>
        {/* Feed Section - Left */}
        {!isMobile && renderFeedSection()}

        {/* Mobile Feed View - FIX: Corrected closing tag */}
        {isMobile && (
          <Box
            sx={{ 
              width: '100%',
              height: '100%', 
              display: mobileSection === 'feed' ? 'flex' : 'none',
              flexDirection: 'column'
            }}
          >
            <SectionHeader>
              <Typography variant="h6" color="white">
                Team Feed
              </Typography>
            </SectionHeader> {/* This was incorrectly closed with SectionContent */}
            <SectionContent>
              <FeedContainer />
            </SectionContent>
          </Box>
        )}
        
        {/* Chat Area (Conversations + Chat) - Right */}
        {!isMobile && renderChatAreaSection()}
        
        {/* Mobile Chat View */}
        {isMobile && (
          <Box 
            sx={{ 
              width: '100%',
              height: '100%', 
              display: mobileSection === 'chat' ? 'flex' : 'none',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Chat Area with Tabs for Mobile */}
            <Paper square elevation={0}>
              <Tabs
                value={activeConversation ? 'chat' : 'conversations'}
                sx={{ minHeight: '56px', borderBottom: '1px solid #333' }}
              >
                <Tab
                  icon={<PeopleIcon />}
                  iconPosition="start"
                  label="Conversations"
                  value="conversations"
                  sx={{ color: 'white', minHeight: '56px' }}
                  onClick={() => setActiveConversation(null)}
                />
                <Tab
                  icon={<ForumIcon />}
                  iconPosition="start"
                  label="Chat"
                  value="chat"
                  sx={{ color: 'white', minHeight: '56px' }}
                  disabled={!activeConversation}
                />
              </Tabs>
            </Paper>

            {/* Conditionally show conversations or chat */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Box 
                sx={{ 
                  height: '100%',
                  display: activeConversation ? 'none' : 'block'
                }}
              >
                <ChatSelector onSelectConversation={handleSelectConversation} />
              </Box>
              <Box 
                sx={{ 
                  height: '100%',
                  display: activeConversation ? 'block' : 'none'
                }}
              >
                <ChatPanel conversation={activeConversation} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </FullWidthContainer>
  );
};

export default Social;
